import Badge from "@/components/Badge";
import Card from "@/components/Card";
import DashboardLayout from "@/components/layouts/Dashboard";
import Freelancer from "@/components/marketplace/Freelancer";
import {Button} from "@/components/ui/button";
import {
  ChevronDown,
  Filter,
  Rocket,
  Zap,
  Shield,
  Users,
  Clock,
  Star,
  AlertCircle
} from "lucide-react";
import {FC} from "react";

interface MarketplaceProps {
}

// const review = [
//   {
//     title: "Experience",
//     icon: <Calendar className="mr-2 font-normal text-[#8F95B2]"/>,
//     textStyles: "text-[#8F95B2] font-semibold",
//   },
//   {
//     title: "Cost",
//     icon: <Wallet className="mr-2 font-normal text-[#8F95B2]"/>,
//     textStyles: "text-[#8F95B2] font-semibold",
//   },
//   {
//     title: "Skill",
//     icon: <PencilRuler className="mr-2 font-normal text-[#8F95B2]"/>,
//     textStyles: "text-[#8F95B2] font-semibold",
//   },
// ];

// const filter = [
//   {
//     title: "Edit",
//   },
//   {
//     title: "Delete",
//   },
// ];

const freelancers = [
  {
    name: "Robert Fox",
    job: "Java Developer",
    country: "USA",
    countryCode: "US",
    countryColor: "bg-[#0052b41a]",
    experience: 14,
    stars: 5,
    cost: 500,
    duration: "2 days",
    start: "ASAP",
    image: "/images/user.jpg",
    skills: (
      <>
        <Badge size="sm" classColor="progress-green">
          JAVA
        </Badge>
        <Badge size="sm" classColor="progress-orange">
          Spring Boot
        </Badge>
        <Badge size="sm" classColor="progress-purple">
          VueJS
        </Badge>
        <Badge size="sm" classColor="progress-red">
          Python
        </Badge>
      </>
    ),
  },
  {
    name: "Salman Khan",
    job: "Java Developer",
    country: "India",
    countryCode: "IN",
    countryColor: "bg-[#ffda444d]",
    experience: 10,
    stars: 4,
    cost: 150,
    duration: "1 days",
    start: "ASAP",
    image: "/images/user2.jpeg",
    skills: (
      <>
        <Badge size="sm" classColor="progress-green">
          JAVA
        </Badge>
        <Badge size="sm" classColor="progress-orange">
          React
        </Badge>
        <Badge size="sm" classColor="progress-purple">
          Kotlin
        </Badge>
        <Badge size="sm" classColor="progress-red">
          AWS
        </Badge>
      </>
    ),
  },
  {
    name: "Jérôme Boucher",
    job: "Java Developer",
    country: "France",
    countryCode: "FR",
    countryColor: "bg-[#FFE9EB]",
    experience: 5,
    stars: 4,
    cost: 450,
    duration: "1 days",
    start: "ASAP",
    image: "/images/user3.jpeg",
    skills: (
      <>
        <Badge size="sm" classColor="progress-green">
          JAVA
        </Badge>
        <Badge size="sm" classColor="progress-orange">
          Python
        </Badge>
        <Badge size="sm" classColor="progress-purple">
          Typescript
        </Badge>
        <Badge size="sm" classColor="progress-red">
          Angular
        </Badge>
      </>
    ),
  },
  {
    name: "Grace T. Frazier",
    job: "Java Developer",
    country: "USA",
    countryCode: "US",
    countryColor: "bg-[#ff981133]",
    experience: 8,
    stars: 4,
    cost: 390,
    duration: "1 days",
    start: "ASAP",
    image: "/images/user4.jpeg",
    skills: (
      <>
        <Badge size="sm" classColor="progress-green">
          JAVA
        </Badge>
        <Badge size="sm" classColor="progress-orange">
          Kotlin
        </Badge>
        <Badge size="sm" classColor="progress-purple">
          Spring
        </Badge>
        <Badge size="sm" classColor="progress-red">
          Javascript
        </Badge>
      </>
    ),
  },
  {
    name: "Peter J. Johnson",
    job: "Java Developer",
    country: "USA",
    countryCode: "US",
    countryColor: "bg-[#ff981133]",
    experience: 12,
    stars: 5,
    cost: 650,
    duration: "2 days",
    start: "ASAP",
    image: "/images/user5.jpeg",
    skills: (
      <>
        <Badge size="sm" classColor="progress-green">
          JAVA
        </Badge>
        <Badge size="sm" classColor="progress-orange">
          React
        </Badge>
        <Badge size="sm" classColor="progress-purple">
          VueJS
        </Badge>
        <Badge size="sm" classColor="progress-red">
          Python
        </Badge>
      </>
    ),
  },
];

const Marketplace: FC<MarketplaceProps> = () => {
  return (
    <DashboardLayout>
      <div className="max-w-7xl m-auto flex flex-col items-stretch grow gap-y-6 h-full w-full">
        
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 rounded-2xl">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="relative z-10 px-8 py-12 text-white">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full font-bold text-sm flex items-center animate-pulse">
                <Rocket className="w-4 h-4 mr-2" />
                Coming Soon
              </div>
            </div>
            <h1 className="text-4xl font-bold text-center mb-4">
              AI-Powered Developer Marketplace
            </h1>
            <p className="text-xl text-center mb-8 max-w-4xl mx-auto text-indigo-100">
              Hire expert developers to collaborate with our AI for faster, more reliable complex backend development cycles. 
              Human expertise meets AI efficiency for unparalleled results.
            </p>
            
            {/* Value Propositions */}
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <Zap className="w-12 h-12 mx-auto mb-3 text-yellow-400" />
                <h3 className="font-semibold mb-2">Lightning Fast</h3>
                <p className="text-sm text-indigo-100">AI generates code, developers validate and optimize 24/24 - cutting development time by 90%</p>
              </div>
              <div className="text-center">
                <Shield className="w-12 h-12 mx-auto mb-3 text-green-400" />
                <h3 className="font-semibold mb-2">Quality & Security</h3>
                <p className="text-sm text-indigo-100">Expert developers ensure code quality, security, and best practices</p>
              </div>
              <div className="text-center">
                <Users className="w-12 h-12 mx-auto mb-3 text-blue-400" />
                <h3 className="font-semibold mb-2">Unlimited Workforce</h3>
                <p className="text-sm text-indigo-100">Scale your team instantly to run multiple parallel projects while maintaining high standards and consistent quality across all deliverables</p>
              </div>
            </div>
          </div>
        </div>

        {/* Preview of Developers */}
        <Card borderColor="[#dbe0f6]" bgColor="white" minHeight="[510px]">
          <div className="p-6 w-full">
            <div className="flex flex-col md:flex-row gap-3 justify-between items-start md:items-center py-4 border-b-[1px] border-[#dbe0f6]">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl flex items-center px-3 py-1 font-bold text-base">
                  <Users className="w-4 h-4 mr-2" />
                  Preview
                </div>
                <div>
                  <h1 className="text-black text-2xl tracking-wide font-bold">
                    Expert Developer Network
                  </h1>
                  <span className="text-[#8F95B2] text-sm font-semibold">
                    AI-Collaborative Specialists
                  </span>
                </div>
              </div>
              <div className="flex gap-3">
                <Button disabled className="bg-gray-100 text-gray-400 border-[1px] text-sm tracking-wider cursor-not-allowed">
                  <Filter className="mr-2"/> Filter (Coming Soon)
                </Button>
                <Button disabled className="bg-gray-100 text-gray-400 border-[1px] text-sm tracking-wider cursor-not-allowed">
                  Sort By <ChevronDown className="ml-2"/>
                </Button>
              </div>
            </div>
            
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center">
                <h4 className="text-base text-black font-bold">Featured AI-Collaborative Developers</h4>
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl px-3 py-1 font-bold text-sm ml-3">
                  <Star className="w-4 h-4 inline mr-1" />
                  Top Rated
                </div>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <AlertCircle className="w-4 h-4 mr-1" />
                Preview Mode - Booking Available Soon
              </div>
            </div>
            
            <div className="grid grid-cols-12 gap-5">
              {freelancers.map((freelancer, index) => (
                <div key={index} className="relative xl:col-span-4 lg:col-span-6 col-span-12">
                  {/* Preview Overlay */}
                  <div className="absolute inset-0 bg-gray-50 bg-opacity-90 rounded-2xl z-10 flex items-center justify-center backdrop-blur-sm">
                    <div className="text-center p-4">
                      <Clock className="w-8 h-8 mx-auto mb-2 text-indigo-600" />
                      <p className="font-semibold text-gray-700">Available Soon</p>
                      <p className="text-xs text-gray-500">Get notified when marketplace opens</p>
                    </div>
                  </div>
                  <Freelancer
                    name={freelancer.name}
                    job={freelancer.job + " + AI Specialist"}
                    country={freelancer.country}
                    countryCode={freelancer.countryCode}
                    countryColor={freelancer.countryColor}
                    experience={freelancer.experience}
                    stars={freelancer.stars}
                    cost={freelancer.cost}
                    duration={freelancer.duration}
                    start={freelancer.start}
                    image={freelancer.image}
                    skills={freelancer.skills}
                  />
                </div>
              ))}
            </div>
          </div>
        </Card>

      </div>
    </DashboardLayout>
  );
};

export default Marketplace;
